/**
 * /pipeline-facility/ai/:id — one AI draft on its own page
 * (GET /pipeline-facility/ai/digests/:id, fieldiq-api#1236). The back arrow
 * returns to the register with its filters.
 *
 * Panels stack full width: the deterministic figures first (the API's, in
 * full), then the AI draft — "Draft — awaiting your approval" with Approve,
 * Edit-then-approve and Dismiss while the run is pending, the approved text
 * once a person has approved it. A run with no draft still shows its figures
 * and says the draft is unavailable, with the API's reason.
 */
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { usePfAiDigest } from '../../../hooks/usePfAi';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import { formatInstant } from '../../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { canUsePfAi, pfAiAgentLabel, pfAiOutcomeSkin, pfAiScopeText } from '../../../lib/pf-ai';
import { pfDay } from '../../../lib/pipeline-facility';
import { useBackToList } from '../../downhole/recordPage';
import { isNotFound, PF_EYEBROW, PfBadge, PfLoading } from '../PfBits';
import { PfAiDraftPanel, RunFigures } from './PfAiBits';

const LIST = '/pipeline-facility/ai';

export default function PfAiDraftDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBackToList(LIST);
  const { user } = useAuth();
  const allowed = canUsePfAi(user?.role);
  const tz = useTenantTimeZone();
  const query = usePfAiDigest(id, allowed);
  const run = query.data ?? null;
  const failure = queryLoadFailure(query);
  const notFound = !run && !query.isLoading && isNotFound(queryFailureError(query));
  const t = run?.triggerData ?? {};
  const ticketId = typeof t.ticketId === 'string' ? t.ticketId : null;
  const jobId = !ticketId && typeof t.jobId === 'string' ? t.jobId : null;

  return (
    <div data-testid="pf-ai-draft-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={`${PF_EYEBROW} · AI draft`}
        title={run ? pfAiAgentLabel(run.agent) : query.isLoading && allowed ? 'Loading…' : 'AI draft'}
        onBack={back}
        backLabel="Back to AI drafts"
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4" data-testid="pf-ai-draft-detail">
          {!allowed ? (
            <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-6 text-sm text-fieldiq-text" data-testid="pf-ai-not-allowed">
              AI drafts are for admins, office staff and supervisors.
            </p>
          ) : !run && query.isLoading ? (
            <PfLoading label="Loading the draft" />
          ) : notFound ? (
            <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-6 text-sm text-fieldiq-text" data-testid="pf-ai-not-found">
              This draft was not found.
            </p>
          ) : !run ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? undefined} subject="this draft" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <PfBadge skin={pfAiOutcomeSkin(run.outcome)} value={run.outcome} />
                <span className="text-fieldiq-text">{pfAiScopeText(String(run.agent), run.triggerData, pfDay)}</span>
                <span className="text-fieldiq-muted">Run {formatInstant(run.createdAt, tz) ?? '—'}</span>
                {run.reviewedAt && <span className="text-fieldiq-muted">· Reviewed {formatInstant(run.reviewedAt, tz) ?? '—'}</span>}
                {ticketId && (
                  <Link to={`/pipeline-facility/tickets/${ticketId}`} className="font-bold text-fieldiq-text underline" data-testid="pf-ai-ticket-link">
                    Open the ticket
                  </Link>
                )}
                {jobId && (
                  <Link to={`/pipeline-facility/jobs/${jobId}`} className="font-bold text-fieldiq-text underline" data-testid="pf-ai-job-link">
                    Open the job
                  </Link>
                )}
              </div>
              {run.outcome === 'superseded' && (
                <p role="status" className="text-xs text-fieldiq-muted">A newer run of this draft replaced this one, so it can no longer be approved.</p>
              )}
              <RunFigures agent={String(run.agent)} data={run.data} />
              <PfAiDraftPanel
                runId={run.agentRunId}
                outcome={run.outcome}
                draft={run.draft}
                approved={run.approved}
                approvedEdited={run.approvedEdited}
                aiError={run.aiError}
                canReview={allowed}
              />
            </>
          )}
        </div>
      </section>
    </div>
  );
}
